// Circuiti bancari — replica lato frontend della logica di manager
// (App\Services\Refund\RefundPayability::for + config/sepa.php).
// Deve restare allineato a food/backend/src/bank/sepa.js.

import { normalizeIban, isValidIban } from './validators';

// Allineato a config/sepa.php ('sepa_countries').
export const SEPA_COUNTRIES = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
    'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU',
    'MT', 'MC', 'NL', 'NO', 'PL', 'PT', 'RO', 'SM', 'SK', 'SI',
    'ES', 'SE', 'CH', 'GB', 'VA', 'AD',
    'AL', 'MD', 'ME', 'MK', 'RS',
];

// Gruppo C: fuori dalla rete SWIFT (config/sepa.php 'no_swift_countries').
export const NO_SWIFT_COUNTRIES = ['KP'];

export type Circuit = 'sepa' | 'extra_sepa' | 'group_c' | 'missing';

// Modalità di inserimento guidata dal SOLO paese selezionato, indipendente dai
// dati già inseriti. Serve a decidere quale campo mostrare (IBAN vs numero conto)
// appena l'utente sceglie la nazione, senza il cortocircuito di classifyCircuit
// (che resta 'missing' finché non c'è già un conto, mostrando sempre l'IBAN).
export type CountryMode = 'sepa' | 'extra_sepa' | 'group_c' | 'none';

/**
 * Classifica il paese per decidere quale campo bancario mostrare.
 * - 'none'       → nessun paese selezionato
 * - 'group_c'    → paese senza circuito SWIFT (rimborso non automatizzabile)
 * - 'sepa'       → area SEPA: si usa l'IBAN
 * - 'extra_sepa' → fuori SEPA: si usa numero conto + BIC
 */
export function countryMode(bankCountry?: string): CountryMode {
    const country = (bankCountry || '').trim().toUpperCase();
    if (country === '') return 'none';
    if (NO_SWIFT_COUNTRIES.includes(country)) return 'group_c';
    if (SEPA_COUNTRIES.includes(country)) return 'sepa';
    return 'extra_sepa';
}

export interface CircuitRequires {
    iban: boolean;
    bic: boolean;
    accountNumber: boolean;
}

export interface CircuitResult {
    circuit: Circuit;
    blockers: string[];
    requires: CircuitRequires;
}

export interface CircuitInput {
    iban?: string;
    account_number?: string;
    swift?: string;
    bank_country?: string;
}

function computeRequires(circuit: Circuit): CircuitRequires {
    switch (circuit) {
        case 'sepa':
            return { iban: true, bic: false, accountNumber: false };
        case 'extra_sepa':
            return { iban: false, bic: true, accountNumber: false };
        default:
            return { iban: false, bic: false, accountNumber: false };
    }
}

/**
 * Replica di RefundPayability::for(). Vedi backend/src/bank/sepa.js.
 */
export function classifyCircuit(input: CircuitInput): CircuitResult {
    const rawIban = (input.iban || '').trim();
    const iban = normalizeIban(rawIban);
    const validIban = isValidIban(iban);
    const account = (input.account_number || '').trim();
    const bic = (input.swift || '').trim();
    const bankCountry = (input.bank_country || '').trim().toUpperCase();

    const country = bankCountry !== '' ? bankCountry : (validIban ? iban.slice(0, 2) : '');
    const hasAccount = validIban || account !== '';

    const blockers: string[] = [];

    if (!hasAccount) {
        blockers.push(
            rawIban !== ''
                ? "Il campo IBAN non contiene un IBAN valido. Se è un conto estero, indica il numero di conto e la nazione della banca."
                : 'Nessun IBAN o numero di conto.'
        );
    }

    if (hasAccount && country === '') {
        blockers.push('Nazione della banca mancante: senza di essa il conto non è instradabile.');
    }

    if (!hasAccount || country === '') {
        return { circuit: 'missing', blockers, requires: computeRequires('missing') };
    }

    if (SEPA_COUNTRIES.includes(country)) {
        if (!validIban) {
            blockers.push('Il circuito SEPA richiede un IBAN valido: il solo numero di conto non è sufficiente.');
        }
        return { circuit: 'sepa', blockers, requires: computeRequires('sepa') };
    }

    if (NO_SWIFT_COUNTRIES.includes(country)) {
        blockers.push('Paese senza circuito SWIFT: il rimborso non può essere gestito automaticamente. Contatta CX.');
        return { circuit: 'group_c', blockers, requires: computeRequires('group_c') };
    }

    if (bic === '') {
        blockers.push('BIC/SWIFT della banca obbligatorio per i pagamenti extra-SEPA.');
        return { circuit: 'extra_sepa', blockers, requires: computeRequires('extra_sepa') };
    }

    if (validIban && SEPA_COUNTRIES.includes(iban.slice(0, 2))) {
        blockers.push('Dati incoerenti: IBAN di area SEPA ma nazione della banca extra-SEPA.');
    }

    return { circuit: 'extra_sepa', blockers, requires: computeRequires('extra_sepa') };
}

// Lista paesi per il <select> "Nazione istituto bancario".
// ISO-2, label bilingue IT / EN. Ordinata per label IT.
export interface Country {
    code: string;
    label: string;
}

// Lista completa ISO-3166-1 alpha-2 (249 territori), label IT / EN.
// Non ordinata qui: viene ordinata a valle per label IT (vedi sort in fondo).
const COUNTRIES_UNSORTED: Country[] = [
    { code: 'AF', label: 'Afghanistan' },
    { code: 'AL', label: 'Albania' },
    { code: 'DZ', label: 'Algeria' },
    { code: 'AS', label: 'Samoa Americane / American Samoa' },
    { code: 'AD', label: 'Andorra' },
    { code: 'AO', label: 'Angola' },
    { code: 'AI', label: 'Anguilla' },
    { code: 'AQ', label: 'Antartide / Antarctica' },
    { code: 'AG', label: 'Antigua e Barbuda / Antigua and Barbuda' },
    { code: 'AR', label: 'Argentina' },
    { code: 'AM', label: 'Armenia' },
    { code: 'AW', label: 'Aruba' },
    { code: 'AU', label: 'Australia' },
    { code: 'AT', label: 'Austria' },
    { code: 'AZ', label: 'Azerbaigian / Azerbaijan' },
    { code: 'BS', label: 'Bahamas' },
    { code: 'BH', label: 'Bahrein / Bahrain' },
    { code: 'BD', label: 'Bangladesh' },
    { code: 'BB', label: 'Barbados' },
    { code: 'BE', label: 'Belgio / Belgium' },
    { code: 'BZ', label: 'Belize' },
    { code: 'BJ', label: 'Benin' },
    { code: 'BM', label: 'Bermuda' },
    { code: 'BT', label: 'Bhutan' },
    { code: 'BY', label: 'Bielorussia / Belarus' },
    { code: 'BO', label: 'Bolivia' },
    { code: 'BQ', label: 'Bonaire, Sint Eustatius e Saba / Bonaire, Sint Eustatius and Saba' },
    { code: 'BA', label: 'Bosnia ed Erzegovina / Bosnia and Herzegovina' },
    { code: 'BW', label: 'Botswana' },
    { code: 'BV', label: 'Isola Bouvet / Bouvet Island' },
    { code: 'BR', label: 'Brasile / Brazil' },
    { code: 'IO', label: "Territorio Britannico dell'Oceano Indiano / British Indian Ocean Territory" },
    { code: 'BN', label: 'Brunei' },
    { code: 'BG', label: 'Bulgaria' },
    { code: 'BF', label: 'Burkina Faso' },
    { code: 'BI', label: 'Burundi' },
    { code: 'CV', label: 'Capo Verde / Cabo Verde' },
    { code: 'KH', label: 'Cambogia / Cambodia' },
    { code: 'CM', label: 'Camerun / Cameroon' },
    { code: 'CA', label: 'Canada' },
    { code: 'KY', label: 'Isole Cayman / Cayman Islands' },
    { code: 'CF', label: 'Repubblica Centrafricana / Central African Republic' },
    { code: 'TD', label: 'Ciad / Chad' },
    { code: 'CL', label: 'Cile / Chile' },
    { code: 'CN', label: 'Cina / China' },
    { code: 'CX', label: 'Isola di Natale / Christmas Island' },
    { code: 'CC', label: 'Isole Cocos / Cocos (Keeling) Islands' },
    { code: 'CO', label: 'Colombia' },
    { code: 'KM', label: 'Comore / Comoros' },
    { code: 'CG', label: 'Congo' },
    { code: 'CD', label: 'Congo (Rep. Dem.) / Congo (DRC)' },
    { code: 'CK', label: 'Isole Cook / Cook Islands' },
    { code: 'CR', label: 'Costa Rica' },
    { code: 'CI', label: "Costa d'Avorio / Ivory Coast" },
    { code: 'HR', label: 'Croazia / Croatia' },
    { code: 'CU', label: 'Cuba' },
    { code: 'CW', label: 'Curaçao' },
    { code: 'CY', label: 'Cipro / Cyprus' },
    { code: 'CZ', label: 'Repubblica Ceca / Czech Republic' },
    { code: 'DK', label: 'Danimarca / Denmark' },
    { code: 'DJ', label: 'Gibuti / Djibouti' },
    { code: 'DM', label: 'Dominica' },
    { code: 'DO', label: 'Repubblica Dominicana / Dominican Republic' },
    { code: 'EC', label: 'Ecuador' },
    { code: 'EG', label: 'Egitto / Egypt' },
    { code: 'SV', label: 'El Salvador' },
    { code: 'GQ', label: 'Guinea Equatoriale / Equatorial Guinea' },
    { code: 'ER', label: 'Eritrea' },
    { code: 'EE', label: 'Estonia' },
    { code: 'SZ', label: 'Eswatini' },
    { code: 'ET', label: 'Etiopia / Ethiopia' },
    { code: 'FK', label: 'Isole Falkland / Falkland Islands' },
    { code: 'FO', label: 'Isole Fær Øer / Faroe Islands' },
    { code: 'FJ', label: 'Figi / Fiji' },
    { code: 'FI', label: 'Finlandia / Finland' },
    { code: 'FR', label: 'Francia / France' },
    { code: 'GF', label: 'Guyana Francese / French Guiana' },
    { code: 'PF', label: 'Polinesia Francese / French Polynesia' },
    { code: 'TF', label: 'Territori Australi Francesi / French Southern Territories' },
    { code: 'GA', label: 'Gabon' },
    { code: 'GM', label: 'Gambia' },
    { code: 'GE', label: 'Georgia' },
    { code: 'DE', label: 'Germania / Germany' },
    { code: 'GH', label: 'Ghana' },
    { code: 'GI', label: 'Gibilterra / Gibraltar' },
    { code: 'GR', label: 'Grecia / Greece' },
    { code: 'GL', label: 'Groenlandia / Greenland' },
    { code: 'GD', label: 'Grenada' },
    { code: 'GP', label: 'Guadalupa / Guadeloupe' },
    { code: 'GU', label: 'Guam' },
    { code: 'GT', label: 'Guatemala' },
    { code: 'GG', label: 'Guernsey' },
    { code: 'GN', label: 'Guinea' },
    { code: 'GW', label: 'Guinea-Bissau' },
    { code: 'GY', label: 'Guyana' },
    { code: 'HT', label: 'Haiti' },
    { code: 'HM', label: 'Isole Heard e McDonald / Heard Island and McDonald Islands' },
    { code: 'HN', label: 'Honduras' },
    { code: 'HK', label: 'Hong Kong' },
    { code: 'HU', label: 'Ungheria / Hungary' },
    { code: 'IS', label: 'Islanda / Iceland' },
    { code: 'IN', label: 'India' },
    { code: 'ID', label: 'Indonesia' },
    { code: 'IR', label: 'Iran' },
    { code: 'IQ', label: 'Iraq' },
    { code: 'IE', label: 'Irlanda / Ireland' },
    { code: 'IM', label: 'Isola di Man / Isle of Man' },
    { code: 'IL', label: 'Israele / Israel' },
    { code: 'IT', label: 'Italia / Italy' },
    { code: 'JM', label: 'Giamaica / Jamaica' },
    { code: 'JP', label: 'Giappone / Japan' },
    { code: 'JE', label: 'Jersey' },
    { code: 'JO', label: 'Giordania / Jordan' },
    { code: 'KZ', label: 'Kazakistan / Kazakhstan' },
    { code: 'KE', label: 'Kenya' },
    { code: 'KI', label: 'Kiribati' },
    { code: 'KP', label: 'Corea del Nord / North Korea' },
    { code: 'KR', label: 'Corea del Sud / South Korea' },
    { code: 'KW', label: 'Kuwait' },
    { code: 'KG', label: 'Kirghizistan / Kyrgyzstan' },
    { code: 'LA', label: 'Laos' },
    { code: 'LV', label: 'Lettonia / Latvia' },
    { code: 'LB', label: 'Libano / Lebanon' },
    { code: 'LS', label: 'Lesotho' },
    { code: 'LR', label: 'Liberia' },
    { code: 'LY', label: 'Libia / Libya' },
    { code: 'LI', label: 'Liechtenstein' },
    { code: 'LT', label: 'Lituania / Lithuania' },
    { code: 'LU', label: 'Lussemburgo / Luxembourg' },
    { code: 'MO', label: 'Macao' },
    { code: 'MG', label: 'Madagascar' },
    { code: 'MW', label: 'Malawi' },
    { code: 'MY', label: 'Malaysia' },
    { code: 'MV', label: 'Maldive / Maldives' },
    { code: 'ML', label: 'Mali' },
    { code: 'MT', label: 'Malta' },
    { code: 'MH', label: 'Isole Marshall / Marshall Islands' },
    { code: 'MQ', label: 'Martinica / Martinique' },
    { code: 'MR', label: 'Mauritania' },
    { code: 'MU', label: 'Mauritius' },
    { code: 'YT', label: 'Mayotte' },
    { code: 'MX', label: 'Messico / Mexico' },
    { code: 'FM', label: 'Micronesia' },
    { code: 'MD', label: 'Moldova' },
    { code: 'MC', label: 'Monaco' },
    { code: 'MN', label: 'Mongolia' },
    { code: 'ME', label: 'Montenegro' },
    { code: 'MS', label: 'Montserrat' },
    { code: 'MA', label: 'Marocco / Morocco' },
    { code: 'MZ', label: 'Mozambico / Mozambique' },
    { code: 'MM', label: 'Myanmar (Birmania) / Myanmar (Burma)' },
    { code: 'NA', label: 'Namibia' },
    { code: 'NR', label: 'Nauru' },
    { code: 'NP', label: 'Nepal' },
    { code: 'NL', label: 'Paesi Bassi / Netherlands' },
    { code: 'NC', label: 'Nuova Caledonia / New Caledonia' },
    { code: 'NZ', label: 'Nuova Zelanda / New Zealand' },
    { code: 'NI', label: 'Nicaragua' },
    { code: 'NE', label: 'Niger' },
    { code: 'NG', label: 'Nigeria' },
    { code: 'NU', label: 'Niue' },
    { code: 'NF', label: 'Isola Norfolk / Norfolk Island' },
    { code: 'MK', label: 'Macedonia del Nord / North Macedonia' },
    { code: 'MP', label: 'Isole Marianne Settentrionali / Northern Mariana Islands' },
    { code: 'NO', label: 'Norvegia / Norway' },
    { code: 'OM', label: 'Oman' },
    { code: 'PK', label: 'Pakistan' },
    { code: 'PW', label: 'Palau' },
    { code: 'PS', label: 'Palestina / Palestine' },
    { code: 'PA', label: 'Panama' },
    { code: 'PG', label: 'Papua Nuova Guinea / Papua New Guinea' },
    { code: 'PY', label: 'Paraguay' },
    { code: 'PE', label: 'Perù / Peru' },
    { code: 'PH', label: 'Filippine / Philippines' },
    { code: 'PN', label: 'Isole Pitcairn / Pitcairn Islands' },
    { code: 'PL', label: 'Polonia / Poland' },
    { code: 'PT', label: 'Portogallo / Portugal' },
    { code: 'PR', label: 'Porto Rico / Puerto Rico' },
    { code: 'QA', label: 'Qatar' },
    { code: 'RE', label: 'Riunione / Réunion' },
    { code: 'RO', label: 'Romania' },
    { code: 'RU', label: 'Russia' },
    { code: 'RW', label: 'Ruanda / Rwanda' },
    { code: 'BL', label: 'Saint-Barthélemy' },
    { code: 'SH', label: "Sant'Elena / Saint Helena" },
    { code: 'KN', label: 'Saint Kitts e Nevis / Saint Kitts and Nevis' },
    { code: 'LC', label: 'Santa Lucia / Saint Lucia' },
    { code: 'MF', label: 'Saint-Martin' },
    { code: 'PM', label: 'Saint-Pierre e Miquelon / Saint Pierre and Miquelon' },
    { code: 'VC', label: 'Saint Vincent e Grenadine / Saint Vincent and the Grenadines' },
    { code: 'WS', label: 'Samoa' },
    { code: 'SM', label: 'San Marino' },
    { code: 'ST', label: 'São Tomé e Príncipe / Sao Tome and Principe' },
    { code: 'SA', label: 'Arabia Saudita / Saudi Arabia' },
    { code: 'SN', label: 'Senegal' },
    { code: 'RS', label: 'Serbia' },
    { code: 'SC', label: 'Seychelles' },
    { code: 'SL', label: 'Sierra Leone' },
    { code: 'SG', label: 'Singapore' },
    { code: 'SX', label: 'Sint Maarten' },
    { code: 'SK', label: 'Slovacchia / Slovakia' },
    { code: 'SI', label: 'Slovenia' },
    { code: 'SB', label: 'Isole Salomone / Solomon Islands' },
    { code: 'SO', label: 'Somalia' },
    { code: 'ZA', label: 'Sudafrica / South Africa' },
    { code: 'GS', label: 'Georgia del Sud e Sandwich Australi / South Georgia and the South Sandwich Islands' },
    { code: 'SS', label: 'Sud Sudan / South Sudan' },
    { code: 'ES', label: 'Spagna / Spain' },
    { code: 'LK', label: 'Sri Lanka' },
    { code: 'SD', label: 'Sudan' },
    { code: 'SR', label: 'Suriname' },
    { code: 'SJ', label: 'Svalbard e Jan Mayen / Svalbard and Jan Mayen' },
    { code: 'SE', label: 'Svezia / Sweden' },
    { code: 'CH', label: 'Svizzera / Switzerland' },
    { code: 'SY', label: 'Siria / Syria' },
    { code: 'TW', label: 'Taiwan' },
    { code: 'TJ', label: 'Tagikistan / Tajikistan' },
    { code: 'TZ', label: 'Tanzania' },
    { code: 'TH', label: 'Thailandia / Thailand' },
    { code: 'TL', label: 'Timor Est / Timor-Leste' },
    { code: 'TG', label: 'Togo' },
    { code: 'TK', label: 'Tokelau' },
    { code: 'TO', label: 'Tonga' },
    { code: 'TT', label: 'Trinidad e Tobago / Trinidad and Tobago' },
    { code: 'TN', label: 'Tunisia' },
    { code: 'TR', label: 'Turchia / Turkey' },
    { code: 'TM', label: 'Turkmenistan' },
    { code: 'TC', label: 'Isole Turks e Caicos / Turks and Caicos Islands' },
    { code: 'TV', label: 'Tuvalu' },
    { code: 'UG', label: 'Uganda' },
    { code: 'UA', label: 'Ucraina / Ukraine' },
    { code: 'AE', label: 'Emirati Arabi Uniti / United Arab Emirates' },
    { code: 'GB', label: 'Regno Unito / United Kingdom' },
    { code: 'US', label: 'Stati Uniti / United States' },
    { code: 'UM', label: 'Isole minori esterne degli USA / U.S. Minor Outlying Islands' },
    { code: 'UY', label: 'Uruguay' },
    { code: 'UZ', label: 'Uzbekistan' },
    { code: 'VU', label: 'Vanuatu' },
    { code: 'VA', label: 'Città del Vaticano / Vatican City' },
    { code: 'VE', label: 'Venezuela' },
    { code: 'VN', label: 'Vietnam' },
    { code: 'VG', label: 'Isole Vergini Britanniche / British Virgin Islands' },
    { code: 'VI', label: 'Isole Vergini Americane / U.S. Virgin Islands' },
    { code: 'WF', label: 'Wallis e Futuna / Wallis and Futuna' },
    { code: 'EH', label: 'Sahara Occidentale / Western Sahara' },
    { code: 'YE', label: 'Yemen' },
    { code: 'ZM', label: 'Zambia' },
    { code: 'ZW', label: 'Zimbabwe' },
];

// Ordinata per label IT (collation locale), così l'elenco è coerente con la UI italiana.
export const COUNTRIES: Country[] = [...COUNTRIES_UNSORTED].sort((a, b) =>
    a.label.localeCompare(b.label, 'it')
);
